import { useState, Fragment, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useI18n } from '../../contexts/I18nContext';
import storageService from '../../services/storage.service';

interface AddCustomDocumentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: {
        document_type: string;
        document_name: string;
        file_url: string;
        file_name: string;
        file_size: number;
        file_type: string;
        notes?: string;
    }) => Promise<void>;
}

export default function AddCustomDocumentModal({
    isOpen,
    onClose,
    onSubmit
}: AddCustomDocumentModalProps) {
    const { language } = useI18n();
    const fileInputRef = useRef<HTMLInputElement>(null);
    
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadedFile, setUploadedFile] = useState<{ 
        name: string; 
        url: string;
        size: number;
        type: string;
    } | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);
    
    const [formData, setFormData] = useState({
        document_name: '',
        document_type: 'other',
        notes: ''
    });

    const documentTypes = [
        { value: 'certificate', labelEn: 'Certificate', labelAr: 'شهادة' },
        { value: 'transcript', labelEn: 'Transcript', labelAr: 'كشف درجات' },
        { value: 'recommendation_letter', labelEn: 'Recommendation Letter', labelAr: 'خطاب توصية' },
        { value: 'motivation_letter', labelEn: 'Motivation Letter', labelAr: 'خطاب الدافع' },
        { value: 'cv_resume', labelEn: 'CV / Resume', labelAr: 'السيرة الذاتية' },
        { value: 'language_certificate', labelEn: 'Language Certificate', labelAr: 'شهادة لغة' },
        { value: 'passport', labelEn: 'Passport Copy', labelAr: 'صورة جواز السفر' },
        { value: 'photo', labelEn: 'Personal Photo', labelAr: 'صورة شخصية' },
        { value: 'other', labelEn: 'Other Document', labelAr: 'مستند آخر' }
    ];

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file size (max 20MB)
        const maxSize = 20 * 1024 * 1024;
        if (file.size > maxSize) {
            setUploadError(language === 'ar' 
                ? 'حجم الملف يجب أن لا يتجاوز 20 ميجابايت' 
                : 'File size must not exceed 20MB');
            return;
        }

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
            setUploadError(language === 'ar' 
                ? 'يرجى رفع صورة (JPG, PNG) أو ملف PDF' 
                : 'Please upload an image (JPG, PNG) or PDF file');
            return;
        }

        setUploadError(null);
        setUploading(true);
        setUploadProgress(10);

        try {
            // Step 1: Get signed URL
            setUploadProgress(30);
            const signedData = await storageService.getSignedUploadUrl(file);

            // Step 2: Upload to GCS
            setUploadProgress(60);
            await storageService.uploadFileToGCS(signedData.upload_url, file);

            // Step 3: Persist metadata
            setUploadProgress(90);
            const persistedFile = await storageService.persistFile({
                object_name: signedData.object_name,
                public_url: signedData.public_url,
                file_name: file.name,
                mime_type: file.type,
                file_size: file.size,
                category: 'documents'
            });

            setUploadProgress(100);
            setUploadedFile({
                name: file.name,
                url: persistedFile.public_url || signedData.public_url,
                size: file.size,
                type: file.type
            });

        } catch (error) {
            console.error('Upload failed:', error);
            setUploadError(language === 'ar' 
                ? 'فشل رفع الملف. يرجى المحاولة مرة أخرى.' 
                : 'Upload failed. Please try again.');
        } finally {
            setUploading(false);
            setUploadProgress(0);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleRemoveFile = () => {
        setUploadedFile(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!uploadedFile) {
            setUploadError(language === 'ar' 
                ? 'يرجى رفع المستند' 
                : 'Please upload a document');
            return;
        }

        if (!formData.document_name.trim()) {
            setUploadError(language === 'ar' 
                ? 'يرجى إدخال اسم المستند' 
                : 'Please enter document name');
            return;
        }

        setLoading(true);
        try {
            await onSubmit({
                document_type: formData.document_type,
                document_name: formData.document_name.trim(),
                file_url: uploadedFile.url,
                file_name: uploadedFile.name,
                file_size: uploadedFile.size,
                file_type: uploadedFile.type,
                notes: formData.notes.trim() || undefined
            });
            // Reset state on success
            resetForm();
            onClose();
        } catch (error) {
            console.error('Error submitting document:', error);
            setUploadError(language === 'ar' 
                ? 'حدث خطأ أثناء إضافة المستند' 
                : 'Error adding document');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            document_name: '',
            document_type: 'other',
            notes: ''
        });
        setUploadedFile(null);
        setUploadError(null);
    };

    const handleClose = () => {
        if (!loading && !uploading) {
            resetForm();
            onClose();
        }
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[9999]" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/25 backdrop-blur-sm z-[9998]" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto z-[9999]">
                    <div className="flex min-h-full items-center justify-center p-2 sm:p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-lg max-h-[85vh] transform overflow-hidden rounded-xl sm:rounded-2xl bg-white shadow-xl transition-all flex flex-col">
                                {/* Header - Fixed */}
                                <div className="px-4 sm:px-5 pt-3 sm:pt-4 pb-2.5 border-b border-gray-100 flex-shrink-0">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-base sm:text-lg font-bold leading-5 text-[#2C2C2C] flex items-center gap-2"
                                    >
                                        <div className="w-8 h-8 bg-gradient-to-br from-[#5B3FD0]/10 to-[#5B3FD0]/5 rounded-lg flex items-center justify-center">
                                            <svg className="w-4 h-4 text-[#5B3FD0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                            </svg>
                                        </div>
                                        {language === 'ar' ? 'إضافة مستند جديد' : 'Add New Document'}
                                    </Dialog.Title>
                                    <p className="text-xs text-[#707070] mt-1">
                                        {language === 'ar' 
                                            ? 'أضف مستندات إضافية لدعم طلبك' 
                                            : 'Add supporting documents to your application'}
                                    </p>
                                </div>

                                {/* Scrollable Content */}
                                <div className="overflow-y-auto flex-1 px-4 sm:px-6 py-3 sm:py-4">
                                    <form id="custom-document-form" onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                                        {/* Document Name */}
                                        <div>
                                            <label className="block text-xs font-bold text-[#2C2C2C] mb-1">
                                                {language === 'ar' ? 'اسم المستند' : 'Document Name'} *
                                            </label>
                                            <input
                                                type="text"
                                                required
                                                value={formData.document_name}
                                                onChange={(e) => setFormData({ ...formData, document_name: e.target.value })}
                                                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#5B3FD0] focus:border-transparent text-sm"
                                                placeholder={language === 'ar' ? 'مثال: شهادة التخرج' : 'e.g., Graduation Certificate'}
                                            />
                                        </div>

                                        {/* Document Type */}
                                        <div>
                                            <label className="block text-xs font-bold text-[#2C2C2C] mb-1">
                                                {language === 'ar' ? 'نوع المستند' : 'Document Type'}
                                            </label>
                                            <select
                                                value={formData.document_type}
                                                onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
                                                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#5B3FD0] focus:border-transparent bg-white text-[#2C2C2C] text-sm"
                                            >
                                                {documentTypes.map((type) => (
                                                    <option key={type.value} value={type.value}>
                                                        {language === 'ar' ? type.labelAr : type.labelEn}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Notes (Optional) */}
                                        <div>
                                            <label className="block text-xs font-bold text-[#2C2C2C] mb-1">
                                                {language === 'ar' ? 'ملاحظات (اختياري)' : 'Notes (Optional)'}
                                            </label>
                                            <textarea
                                                value={formData.notes}
                                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#5B3FD0] focus:border-transparent text-sm resize-none"
                                                rows={2}
                                                placeholder={language === 'ar' ? 'أي ملاحظات إضافية...' : 'Any additional notes...'}
                                            />
                                        </div>

                                        {/* File Upload Section */}
                                        <div>
                                            <label className="block text-xs font-bold text-[#2C2C2C] mb-1">
                                                {language === 'ar' ? 'الملف' : 'File'} *
                                            </label>
                                            
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                onChange={handleFileSelect}
                                                accept=".pdf,.jpg,.jpeg,.png"
                                                className="hidden"
                                            />

                                            {/* Upload Progress */}
                                            {uploading && (
                                                <div className="p-2.5 bg-gray-50 rounded-lg border border-gray-200">
                                                    <div className="flex items-center gap-2 mb-1.5">
                                                        <svg className="animate-spin h-4 w-4 text-[#5B3FD0]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        <span className="text-xs font-medium text-[#2C2C2C]">
                                                            {language === 'ar' ? 'جاري رفع الملف...' : 'Uploading...'}
                                                        </span>
                                                    </div>
                                                    <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                                        <div 
                                                            className="h-full bg-[#5B3FD0] transition-all duration-300"
                                                            style={{ width: `${uploadProgress}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {/* Uploaded File Display */}
                                            {uploadedFile && !uploading && (
                                                <div className="p-2.5 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between gap-2">
                                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <p className="text-xs font-bold text-green-800 truncate">
                                                                {uploadedFile.name}
                                                            </p>
                                                            <p className="text-xs text-green-600">
                                                                {language === 'ar' ? 'تم الرفع بنجاح' : 'Uploaded successfully'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={handleRemoveFile}
                                                        className="p-1.5 hover:bg-green-100 rounded-lg transition-colors flex-shrink-0"
                                                    >
                                                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            )}

                                            {/* Upload Button */}
                                            {!uploadedFile && !uploading && (
                                                <button
                                                    type="button"
                                                    onClick={() => fileInputRef.current?.click()}
                                                    className="w-full p-3 sm:p-4 border-2 border-dashed border-gray-300 hover:border-[#5B3FD0] rounded-lg transition-colors group"
                                                >
                                                    <div className="flex flex-col items-center gap-2">
                                                        <div className="w-10 h-10 bg-gray-100 group-hover:bg-[#5B3FD0]/10 rounded-lg flex items-center justify-center transition-colors">
                                                            <svg className="w-5 h-5 text-[#707070] group-hover:text-[#5B3FD0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                            </svg>
                                                        </div>
                                                        <div className="text-center">
                                                            <p className="text-xs font-bold text-[#2C2C2C] group-hover:text-[#5B3FD0]">
                                                                {language === 'ar' ? 'اضغط لرفع الملف' : 'Click to upload file'}
                                                            </p>
                                                            <p className="text-xs text-[#707070] mt-0.5">
                                                                {language === 'ar' ? 'PDF, JPG, PNG (حتى 10MB)' : 'PDF, JPG, PNG (up to 10MB)'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </button>
                                            )}

                                            {/* Error Message */}
                                            {uploadError && (
                                                <p className="mt-1.5 text-xs text-red-500 font-medium flex items-center gap-1">
                                                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <span>{uploadError}</span>
                                                </p>
                                            )}
                                        </div>
                                    </form>
                                </div>

                                {/* Footer - Fixed */}
                                <div className="px-4 sm:px-5 py-2.5 sm:py-3 border-t border-gray-100 flex-shrink-0 bg-gray-50/50">
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={handleClose}
                                            disabled={loading || uploading}
                                            className="flex-1 px-3 py-2 bg-white border border-gray-200 text-[#707070] rounded-lg font-bold hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm"
                                        >
                                            {language === 'ar' ? 'إلغاء' : 'Cancel'}
                                        </button>
                                        <button
                                            type="submit"
                                            form="custom-document-form"
                                            disabled={loading || uploading || !uploadedFile || !formData.document_name.trim()}
                                            className="flex-1 px-3 py-2 bg-[#5B3FD0] text-white rounded-lg font-bold hover:bg-[#4A2FB0] transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5 text-sm"
                                        >
                                            {loading ? (
                                                <>
                                                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    <span>{language === 'ar' ? 'جاري الإضافة...' : 'Adding...'}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                    </svg>
                                                    <span>{language === 'ar' ? 'إضافة المستند' : 'Add Document'}</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
